import React, { useEffect, useState } from 'react';
import { X, ShieldCheck } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const fullUrl = (u) => (u && u.startsWith('/') ? `${API}${u}` : u);

export default function CoaViewer({ images = [], productName = '', onClose }) {
  const [obscured, setObscured] = useState(false);
  const email = (localStorage.getItem('alp_library_email') || '').trim();
  const stamp = `${email || 'AUSTRALIAN LUXE PEPTIDES'} \u00b7 ${new Date().toLocaleDateString()}`;

  useEffect(() => {
    const block = (e) => { e.preventDefault(); return false; };
    const onKey = (e) => {
      const k = (e.key || '').toLowerCase();
      if ((e.ctrlKey || e.metaKey) && ['s', 'p', 'c', 'u', 'a'].includes(k)) { e.preventDefault(); return false; }
      if (k === 'printscreen') {
        try { navigator.clipboard.writeText(''); } catch (_) { /* noop */ }
        setObscured(true); setTimeout(() => setObscured(false), 1500);
      }
      if (k === 'escape') onClose?.();
      return undefined;
    };
    const onBlur = () => setObscured(true);
    const onFocus = () => setObscured(false);
    const onVis = () => setObscured(document.hidden);
    document.addEventListener('contextmenu', block);
    document.addEventListener('copy', block);
    document.addEventListener('dragstart', block);
    document.addEventListener('keydown', onKey);
    window.addEventListener('blur', onBlur);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('contextmenu', block);
      document.removeEventListener('copy', block);
      document.removeEventListener('dragstart', block);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div data-testid="coa-viewer" className="fixed inset-0 z-[70] bg-alp-black/95 backdrop-blur-sm overflow-y-auto" style={{ userSelect: 'none', WebkitUserSelect: 'none', MozUserSelect: 'none' }}>
      <style>{`@media print { body { display: none !important; } }`}</style>
      <div className="sticky top-0 z-10 flex items-center justify-between px-5 md:px-8 py-4 border-b border-alp-line bg-alp-black/95">
        <div className="flex items-center gap-2 text-alp-gold text-xs tracking-[0.2em] uppercase">
          <ShieldCheck size={16} /> Certificate of Analysis{productName ? ` \u00b7 ${productName}` : ''}
        </div>
        <button data-testid="coa-viewer-close" onClick={onClose} className="text-alp-cream hover:text-alp-gold p-2 transition-colors"><X size={22} /></button>
      </div>
      <p className="text-center text-alp-gray text-[11px] tracking-[0.15em] uppercase py-3">View only &middot; Downloading, saving &amp; printing are disabled</p>

      <div className="max-w-3xl mx-auto px-4 pb-16 space-y-6">
        {images.map((u, i) => (
          <div key={i} data-testid={`coa-page-${i}`} className="relative w-full rounded overflow-hidden border border-alp-line bg-white" style={{ aspectRatio: '1 / 1.414' }}>
            <div className="absolute inset-0 bg-center bg-contain bg-no-repeat" style={{ backgroundImage: `url("${fullUrl(u)}")`, pointerEvents: 'none' }} />
            <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden opacity-[0.16] flex items-center justify-center" style={{ transform: 'rotate(-28deg)' }}>
              <div className="whitespace-nowrap text-alp-black text-xs leading-[3rem]">
                {Array.from({ length: 26 }).map((_, r) => (
                  <div key={r}>{Array.from({ length: 10 }).map((__, c) => (<span key={c} className="mx-4">{stamp}</span>))}</div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      {obscured && (
        <div className="fixed inset-0 z-[80] bg-alp-black flex items-center justify-center" data-testid="coa-obscured">
          <p className="text-alp-gold text-sm tracking-[0.2em] uppercase">Content hidden</p>
        </div>
      )}
    </div>
  );
}
